import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { logIn, signUp } from '../auth';
import { getUserFacingError } from '../lib/errors';
import { isSupabaseConfigured } from '../supabaseClient';

const initialState = {
  fullName: '',
  email: '',
  password: '',
};

export function LoginPage({ currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';
  const redirectParam = searchParams.get('redirect');
  const redirectPath =
    redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
      ? redirectParam
      : '/';
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');

  useEffect(() => {
    setMode(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  }, [searchParams]);

  if (currentUser) {
    return <Navigate replace to={redirectPath} />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus('');
    setConfirmationEmail('');
    setSubmitting(true);

    try {
      if (mode === 'signin') {
        await logIn(form.email, form.password);
        setStatus('Signed in.');
      } else {
        const submittedEmail = form.email;
        await signUp(form.email, form.password, form.fullName);
        setConfirmationEmail(submittedEmail);
      }
      setForm(initialState);
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to complete sign in right now. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode() {
    const nextMode = mode === 'signin' ? 'signup' : 'signin';
    setMode(nextMode);
    setStatus('');
    setConfirmationEmail('');

    const nextParams = new URLSearchParams(searchParams);
    if (nextMode === 'signup') {
      nextParams.set('mode', 'signup');
    } else {
      nextParams.delete('mode');
    }
    setSearchParams(nextParams);
  }

  return (
    <section className="page-shell login-page">
      <div className={`auth-card login-card${submitting || confirmationEmail ? ' is-submitting' : ''}`}>
        {submitting ? (
          <div className="login-loading-state" role="status" aria-live="polite">
            <div className="loader" aria-hidden="true" />
            <span>{mode === 'signin' ? 'Signing in' : 'Creating account'}</span>
          </div>
        ) : confirmationEmail ? (
          <div className="login-confirmation-state" role="status" aria-live="polite">
            <span className="eyebrow">Email confirmation</span>
            <h1>Confirm your email</h1>
            <p>
              We sent a confirmation link to <strong>{confirmationEmail}</strong>. Open that email to activate your
              AgenticHire account, then come back and sign in.
            </p>
            <button type="button" className="primary-button ui-button full-width" onClick={switchMode}>
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            <div className="login-card-head">
              <span className="eyebrow">Login</span>
              <h1>{mode === 'signin' ? 'Sign in' : 'Create your seller account'}</h1>
              <p>
                {mode === 'signin'
                  ? 'Use your email and password to access your marketplace account.'
                  : 'Create an account to publish agent listings, manage packages, and receive buyer requests.'}
              </p>
            </div>

            {!isSupabaseConfigured ? (
              <p className="status-note">
                Supabase is not configured yet. Add your project values to `.env` or `.env.example`.
              </p>
            ) : null}

            <form className="auth-form ui-form" onSubmit={handleSubmit}>
              {mode === 'signup' ? (
                <input
                  className="ui-input"
                  type="text"
                  placeholder="Full name"
                  value={form.fullName}
                  onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
                  required
                />
              ) : null}

              <input
                className="ui-input"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                required
              />

              <input
                className="ui-input"
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                required
              />

              <button type="submit" className="primary-button ui-button full-width">
                {mode === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            <div className="auth-actions login-actions">
              <button
                type="button"
                className="text-button ui-text-button login-switch"
                onClick={switchMode}
              >
                {mode === 'signin' ? 'Need an account?' : 'Already have an account?'}
              </button>
            </div>

            {status ? <p className="status-note">{status}</p> : null}
          </>
        )}
      </div>
    </section>
  );
}
